import { connectedDestination } from '@/lib/planning/connectedDestination'
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Inbox, MessageSquare, Search, UserRound } from 'lucide-react'
import { requestPlanFromPaper } from '@/lib/planFromPaperSignal'
import { HOME_ROUTE } from '@/apps/overview/route'
import { appRegistry } from '@/shell/appRegistry'
import { MORE_GROUPS, isDestinationActive } from './moreDestinations'
import { HelpDialogs } from './HelpDialogs'
import { BackButton } from './BackButton'

const ROUTINES_ROUTE = '/routines'

export const DesktopControlsContext = createContext<HTMLElement | null>(null)
export function DesktopPageControls({ children }: { children: ReactNode }) {
  const host = useContext(DesktopControlsContext)
  return host ? createPortal(children, host) : <>{children}</>
}
/** The slot right of the ☰: a page's one quiet reading (Today's weather). */
export const DesktopLeadContext = createContext<HTMLElement | null>(null)
/** The bar's centre: the horizon rail (PlanNavigation portals it here), so the
 *  bar is one row — ☰ · rail · you (Scott, 2026-09-30). */
export const DesktopCenterContext = createContext<HTMLElement | null>(null)

export function DesktopNavigation({ inboxCount, discussionsUnread, onSearch, onSignOut, userName, controlsRef, leadRef, centerRef, auxiliaryControls }: {
  /** discussionsUnread: the discussions waiting on you (2026-10-14). */
  inboxCount: number; discussionsUnread: number; onSearch: () => void; onSignOut: () => void
  auxiliaryControls?: ReactNode; userName?: string; paused: boolean; controlsRef: (node: HTMLDivElement | null) => void
  leadRef?: (node: HTMLDivElement | null) => void
  centerRef?: (node: HTMLDivElement | null) => void
}) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [open, setOpen] = useState<string | null>(null)
  const [dialog, setDialog] = useState<{ kind: 'shortcuts' | 'help'; from: HTMLElement | null } | null>(null)
  const root = useRef<HTMLElement>(null)
  const trigger = useRef<HTMLButtonElement | null>(null)
  useEffect(() => { setOpen(null) }, [pathname])
  useEffect(() => {
    if (!open) return
    const outside = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(null) }
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(null); trigger.current?.focus() } }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape) }
  }, [open])
  function menu(id: string, label: ReactNode, content: ReactNode, active = false, opts: { ariaLabel?: string; chevron?: boolean; className?: string } = {}) {
    return <div className={`page-navigation-menu${opts.className ? ` ${opts.className}` : ''}`}>
      <button type="button" aria-expanded={open === id} aria-controls={`navigation-${id}`} className={active ? 'is-current' : ''} aria-label={opts.ariaLabel} title={opts.ariaLabel}
        onClick={e => { trigger.current = e.currentTarget; setOpen(open === id ? null : id) }}>{label}{opts.chevron !== false && <ChevronDown size={12} aria-hidden="true" />}</button>
      {open === id && <div id={`navigation-${id}`} className="page-navigation-popover">{content}</div>}
    </div>
  }
  const go = (path: string) => { setOpen(null); navigate(connectedDestination(path, search)) }
  // Short labelled columns rather than one tall list. Registry apps join Reference.
  // Routines is on the bar itself (below), so the menu leaves it out.
  const groups: [string, [string, string][]][] = MORE_GROUPS.map(([group, items]): [string, [string, string][]] => [group, [
    ...items.filter(({ route }) => route !== ROUTINES_ROUTE).map(({ label, route }): [string, string] => [label, route]),
    ...(group === 'Reference' ? appRegistry.filter(a => a.sidebar).sort((a, b) => a.sidebar!.order - b.sidebar!.order)
      .map((a): [string, string] => [a.sidebar!.label, a.route]) : []),
  ]])
  const destinations = groups.flatMap(([, items]) => items)
  return <nav ref={root} className="page-navigation" aria-label="Main navigation">
    {/* No "Planner" here (Scott, 2026-09-28): the horizon rail is the main
        navigation. Routines sits at the rail's end (Scott, 2026-10-24: "add
        routines to the top rail instead of having them buried in the
        hamburger"); before that it lived under More.
        One bar on every page (2026-09-29): the menu at the left; at the right
        Discussions, search and you — page controls live in the page heading. No Add
        button (Scott, 2026-09-29): the round + and ⌘K already add. */}
    <div className="page-navigation-left">
    {/* The app's mark anchors the bar's corner (Scott, 2026-10-02); it goes
        Home, the page you come back to for your bearings (2026-10-11). */}
    <NavLink to={connectedDestination(HOME_ROUTE, search)} className="page-navigation-brand" aria-label="Symphony, go to Home" title="Home">
      <img src="/symphony-logo.png" alt="" width={32} height={32} /><span className="page-navigation-wordmark">Symphony</span>
    </NavLink>
    <BackButton />
    {leadRef && <div ref={leadRef} className="page-navigation-lead" />}
    </div>
    {/* The rail sits in the bar's centre; "More" closes it, a word like the
        rail's own, just right of Routines (Scott, 2026-10-31). Before that a
        ☰ opened the rail (2026-10-03). */}
    <div className="page-navigation-mid">
    {/* Inbox opens the rail, just before Today (Scott, 2026-10-17): the
        captures not yet planned come before the smallest horizon. */}
    <NavLink to={connectedDestination('/inbox', search)} className="page-navigation-icon" title="Inbox"
      data-guide-anchor="inbox" aria-label={`Inbox${inboxCount ? `, ${inboxCount} ${inboxCount === 1 ? 'item' : 'items'}` : ''}`}>
      <Inbox size={17} aria-hidden="true" />{inboxCount > 0 && <span className="navigation-count">{inboxCount}</span>}
    </NavLink>
    {centerRef && <div ref={centerRef} className="page-navigation-center" />}
    <NavLink to={connectedDestination(ROUTINES_ROUTE, search)} className={({ isActive }) => `page-navigation-routines${isActive ? ' is-current' : ''}`}>
      Routines
    </NavLink>
    {menu('more', 'More', <div className="page-navigation-more">
      <div className="page-navigation-groups">
        {groups.map(([group, items]) => <div key={group} role="group" aria-labelledby={`navigation-group-${group}`} className="page-navigation-group">
          <h3 id={`navigation-group-${group}`}>{group}</h3>
          {items.map(([label, route]) => <button key={route} onClick={() => go(route)} aria-current={isDestinationActive(route, pathname) ? 'page' : undefined}>
            {label}
          </button>)}
        </div>)}
      </div>
      <div className="page-navigation-more-action">
        <button onClick={() => { setOpen(null); if (!requestPlanFromPaper()) navigate(connectedDestination('/today', search)) }}>Plan from paper</button>
        <span>Photograph a paper page into your plan</span>
      </div>
      {/* Shortcuts and Help moved here from the footer (Scott, 2026-10-02),
          so the landscape at the foot of the page stays clear. */}
      <div className="page-navigation-more-help">
        <button onClick={() => { setOpen(null); setDialog({ kind: 'shortcuts', from: trigger.current }) }}>Keyboard shortcuts</button>
        <button onClick={() => { setOpen(null); setDialog({ kind: 'help', from: trigger.current }) }}>Help</button>
      </div>
    </div>, destinations.some(([, route]) => isDestinationActive(route, pathname)),
      { className: 'is-more' })}
    </div>
    <div className="page-navigation-utilities">
      {auxiliaryControls}
      <div ref={controlsRef} className="page-navigation-page-controls" />
      {/* Discussions, counting the ones waiting on you (Scott, 2026-10-14:
          "there's no place … you're notified of new or ongoing
          discussions"). Was a count on the menu. */}
      <NavLink to={connectedDestination('/discussions', search)} className="page-navigation-icon" title="Discussions"
        aria-label={`Discussions${discussionsUnread ? `, ${discussionsUnread} waiting on you` : ''}`}>
        <MessageSquare size={17} aria-hidden="true" />{discussionsUnread > 0 && <span className="navigation-count is-waiting">{discussionsUnread}</span>}
      </NavLink>
      <button onClick={onSearch} aria-label="Search" title="Search" className="page-navigation-icon"><Search size={17} aria-hidden="true" /></button>
      {menu('account', <><UserRound size={17} aria-hidden="true" /><span className="sr-only">Account</span></>, <>
        {userName && <p>{userName}</p>}<button onClick={() => go('/settings')}>Settings</button><button onClick={() => { setOpen(null); onSignOut() }}>Sign out</button>
      </>, false, { chevron: false, className: 'is-account' })}
    </div>
    <HelpDialogs open={dialog?.kind ?? null} onClose={() => setDialog(null)} returnFocus={dialog?.from} />
  </nav>
}
